import React, { useState, useEffect } from 'react';
import {
    PencilRuler,
    Wand2,
    Layers,
    PenSquare,
    Users,
    SearchCode,
    Send,
    Trash2,
    Box,
    FileText,
    Briefcase,
    Eye, 
    Edit
} from 'lucide-react';
import apiService from '../../services/apiService';
import Modal from '../../components/Modal';

// --- Reusable UI Components ---

// STYLING: Updated button variants for consistency.
const Button = ({ children, onClick, variant = 'secondary', icon: Icon, disabled = false }) => {
    const baseClasses = 'flex items-center justify-center gap-2 px-3 py-1.5 rounded-md font-semibold text-sm transition-colors disabled:opacity-50';
    const variants = {
        primary: 'bg-brand-500 text-white hover:bg-brand-400',
        secondary: 'bg-slate-600 text-slate-100 hover:bg-slate-500',
        danger: 'bg-red-600/20 text-red-400 hover:bg-red-500/30',
        ghost: 'bg-transparent text-slate-300 hover:bg-slate-700 hover:text-white',
    };
    return (
        <button onClick={onClick} disabled={disabled} className={`${baseClasses} ${variants[variant]}`}>
            {Icon && <Icon size={16} />}
            {children}
        </button>
    );
};

// STYLING: Updated ToolTile to have a more subtle and modern hover effect with a glowing border.
const ToolTile = ({ icon: Icon, title, description, color, onClick }) => (
    <button
        onClick={onClick}
        disabled={!onClick}
        className={`relative p-4 rounded-xl text-left overflow-hidden group transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 bg-slate-800/60 border border-slate-700 hover:border-${color}-500/50 disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-${color}-500/10`}
    >
        <div className="relative z-10">
            <div className={`mb-3 p-2 inline-block rounded-lg bg-${color}-500/10`}>
                <Icon size={24} className={`text-${color}-400`} />
            </div>
            <h4 className="font-bold text-white mb-1">{title}</h4>
            <p className="text-xs text-slate-400">{description}</p>
        </div>
    </button>
);


// --- Modals for Cargo Bay (No style changes needed for modals) ---
const AssetViewModal = ({ asset, onClose }) => (
    <Modal isOpen={true} onClose={onClose} title={asset.title}>
        <div className="prose prose-invert max-w-none bg-slate-900/50 p-4 rounded-md">
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-200">{asset.content}</pre>
        </div>
        <div className="mt-4 flex justify-end">
            <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
    </Modal>
);

const AssetEditModal = ({ asset, onSave, onClose }) => {
    const [title, setTitle] = useState(asset.title);
    const [content, setContent] = useState(asset.content);
    const handleSave = () => onSave(asset._id, { title, content });
    return (
        <Modal isOpen={true} onClose={onClose} title="Edit Asset">
            <div className="space-y-4">
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-slate-700 border border-slate-600 rounded-md p-2" />
                <textarea value={content} onChange={(e) => setContent(e.target.value)} rows="10" className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 resize-y"></textarea>
            </div>
            <div className="mt-4 flex justify-end gap-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={handleSave}>Save Changes</Button>
            </div>
        </Modal>
    );
};

// --- Cargo Bay Component ---
const CargoBay = ({ activeView, setDrafts }) => {
    // ... (logic remains the same)
    const [assets, setAssets] = useState([]);
    const [groupedAssets, setGroupedAssets] = useState({});
    const [activeTab, setActiveTab] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [viewingAsset, setViewingAsset] = useState(null);
    const [editingAsset, setEditingAsset] = useState(null);
    const [toastMessage, setToastMessage] = useState('');

    const loadAssets = async () => {
        setIsLoading(true);
        try {
            const fetchedAssets = await apiService.fetchPublishedAssets();
            const publishedAssets = fetchedAssets.filter(a => a.status === 'Published');
            const draftAssets = fetchedAssets.filter(a => a.status === 'Draft');
            setAssets(publishedAssets);
            setDrafts(draftAssets);
            const grouped = publishedAssets.reduce((acc, asset) => {
                const type = asset.contentType;
                const product = asset.associatedProduct ? asset.associatedProduct.title : 'Uncategorized';
                if (!acc[type]) acc[type] = {};
                if (!acc[type][product]) acc[type][product] = [];
                acc[type][product].push(asset);
                return acc;
            }, {});
            setGroupedAssets(grouped);
            if (Object.keys(grouped).length > 0) {
                setActiveTab(Object.keys(grouped)[0]);
            }
        } catch (error) {
            console.error("Failed to load assets for Cargo Bay:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (activeView === 'foundry') {
            loadAssets();
        }
    }, [activeView]);

    useEffect(() => {
        if (toastMessage) {
            const timer = setTimeout(() => setToastMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [toastMessage]);

    const handleDelete = async (assetId) => {
        if (window.confirm('Are you sure you want to permanently delete this asset?')) {
            try {
                await apiService.deletePublishedAsset(assetId);
                loadAssets();
                setToastMessage('Asset deleted.');
            } catch (error) {
                alert(`Error deleting asset: ${error.message}`);
            }
        }
    };

    const handleSaveEdit = async (assetId, data) => {
        try {
            await apiService.updatePublishedAsset(assetId, data);
            setEditingAsset(null);
            loadAssets();
            setToastMessage('Asset updated.');
        } catch (error) {
            alert(`Error updating asset: ${error.message}`);
        }
    };
    
    const handleSendToFlightDeck = async (assetId) => {
        try {
            await apiService.sendAssetToFlightDeck(assetId);
            setToastMessage('Asset sent to Flight Deck!');
        } catch (error) {
            alert(`Error: ${error.message}`);
        }
    };

    if (isLoading) {
        return <p className="text-slate-400">Loading Cargo Bay...</p>;
    }
    
    return (
        <div className="relative">
            {toastMessage && <div className="absolute top-0 right-0 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg z-10">{toastMessage}</div>}
            {assets.length === 0 ? (
                <div className="text-center p-6 bg-slate-900/50 rounded-lg">
                    <Box size={40} className="mx-auto text-slate-500" />
                    <h3 className="mt-2 font-bold text-white">Cargo Bay is Empty</h3>
                    <p className="text-sm text-slate-400">Published content will appear here.</p>
                </div>
            ) : (
                <div className="flex flex-col">
                    <div className="flex items-center border-b border-slate-700/50 mb-4">
                        {Object.keys(groupedAssets).map(type => (
                            <button key={type} onClick={() => setActiveTab(type)} className={`flex items-center gap-2 px-4 py-2 font-semibold transition-colors ${activeTab === type ? 'border-b-2 border-brand-400 text-brand-300' : 'text-slate-400 hover:text-white'}`}>{type}</button>
                        ))}
                    </div>
                    <div className="space-y-4">
                        {activeTab && groupedAssets[activeTab] && Object.entries(groupedAssets[activeTab]).map(([productName, productAssets]) => (
                            <div key={productName}>
                                <h3 className="flex items-center gap-2 font-semibold text-slate-300 mb-2"><Briefcase size={16} className="text-slate-500" />{productName}</h3>
                                <div className="space-y-2 pl-4 border-l-2 border-slate-800">
                                    {productAssets.map(asset => (
                                         // STYLING: Added glow-on-hover effect to asset items.
                                        <div key={asset._id} className="group relative rounded-lg p-3 transition-all duration-300 hover:bg-slate-800/80 hover:-translate-y-px overflow-hidden flex items-center justify-between gap-3">
                                            <div className="absolute -inset-px rounded-lg bg-gradient-to-r from-brand-500 to-cyan-500 opacity-0 group-hover:opacity-40 transition-opacity duration-300 blur-md"></div>
                                            <div className="relative flex items-center gap-3 w-full"><FileText size={20} className="text-slate-400 flex-shrink-0" /><p className="text-slate-100 font-medium">{asset.title}</p></div>
                                            <div className="relative flex items-center gap-1 flex-shrink-0">
                                                <button onClick={() => setViewingAsset(asset)} title="View" className="p-2 text-slate-400 hover:text-white"><Eye size={16} /></button>
                                                <button onClick={() => setEditingAsset(asset)} title="Edit" className="p-2 text-slate-400 hover:text-white"><Edit size={16} /></button>
                                                <button onClick={() => handleDelete(asset._id)} title="Delete" className="p-2 text-slate-400 hover:text-red-400"><Trash2 size={16} /></button>
                                                <button onClick={() => handleSendToFlightDeck(asset._id)} title="Send to Flight Deck" className="p-2 text-slate-400 hover:text-brand-400"><Send size={16} /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {viewingAsset && <AssetViewModal asset={viewingAsset} onClose={() => setViewingAsset(null)} />}
            {editingAsset && <AssetEditModal asset={editingAsset} onClose={() => setEditingAsset(null)} onSave={handleSaveEdit} />}
        </div>
    );
};

// --- Main Foundry Dashboard View ---
const FoundryDashboardView = ({ setActiveView, setEditingDraft, activeView }) => {
    const [drafts, setDrafts] = useState([]);
    
    const handleContinueEditing = (draft) => {
        setEditingDraft(draft);
        setActiveView('foundryContentCreation');
    };

    return (
        // STYLING: Updated main padding and fade-in animation.
        <div className="p-4 sm:p-6 md:p-8 animate-fade-in">
            <header className="border-b border-slate-800/50 pb-5 mb-8">
                {/* STYLING: Applied gradient title effect. */}
                <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">Foundry</h1>
                <p className="text-lg text-slate-400 mt-2">Your hub for creating, analyzing, and building.</p>
            </header>
            
            {/* STYLING: Increased grid gap for more spacing. */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 flex flex-col gap-8">
                    {/* STYLING: Updated main panel card styles. */}
                    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
                        <h2 className="flex items-center gap-3 text-2xl font-bold text-white mb-4"><Box className="text-brand-400" /> Cargo Bay</h2>
                        <CargoBay activeView={activeView} setDrafts={setDrafts} />
                    </div>
                    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
                        <h2 className="flex items-center gap-3 text-2xl font-bold text-white mb-4"><PencilRuler className="text-yellow-400" /> My Drafts</h2>
                        <div className="space-y-2">
                            {drafts.length > 0 ? drafts.map(draft => (
                                // STYLING: Applied glow-on-hover effect to draft items.
                                <div key={draft._id} className="group relative bg-slate-900/50 p-3 rounded-lg flex items-center justify-between gap-3 overflow-hidden hover:bg-slate-800/60">
                                     <div className="absolute -inset-px rounded-lg bg-gradient-to-r from-yellow-500 to-cyan-500 opacity-0 group-hover:opacity-40 transition-opacity duration-300 blur-md"></div>
                                    <div className="relative">
                                        <p className="text-slate-100 font-medium">{draft.title}</p>
                                        <p className="text-xs text-slate-400">Updated {new Date(draft.updatedAt).toLocaleDateString()}</p>
                                    </div>
                                    <Button variant="ghost" onClick={() => handleContinueEditing(draft)}>Continue Editing</Button>
                                </div>
                            )) : (
                                <p className="text-slate-400 text-sm p-4 text-center">No drafts found.</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-1">
                     {/* STYLING: Updated main panel card styles. */}
                    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 h-full">
                        <h2 className="flex items-center gap-3 text-2xl font-bold text-white mb-4"><Wand2 className="text-purple-400" /> Foundry Tools</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                            <ToolTile icon={Layers} title="Campaign Builder" description="Plan multi-channel campaigns." color="brand" onClick={() => setActiveView('campaigns')} />
                            <ToolTile icon={PenSquare} title="Content Creator" description="Write articles, posts, and more." color="green" onClick={() => setActiveView('foundryContentCreation')} />
                            <ToolTile icon={Users} title="Competitor Analysis" description="Analyze competitor strategies." color="yellow" onClick={() => setActiveView('foundryCompetitorAnalysis')} />
                            <ToolTile icon={SearchCode} title="SEO Audit" description="Find and fix SEO issues." color="red" onClick={() => setActiveView('seoAudit')} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FoundryDashboardView;